import { Link } from "@chakra-ui/react";
import React, { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Text } from "adminComponents/atoms/Text";
import i18n from "lib/i18n";
import { AgreementType, IAgreement } from "lib/types";
import { getBrandConditionedAgreementUrl } from "lib/util";

export interface IAgreementsTextProps {
  agreements: IAgreement[];
  buttonLabel: string;
}

export const useAgreementsText = ({
  agreements,
  buttonLabel,
}: IAgreementsTextProps): ReactNode => {
  const { t } = useTranslation("admin", {
    useSuspense: false,
    keyPrefix: "agreements",
  });

  const agreementsMinusCOPPA = useMemo(() => {
    return agreements.filter(
      (agreement) => agreement.agreement_type !== AgreementType.COPPA
    );
  }, [agreements]);

  const agreementsListMinusCOPPA = useMemo(() => {
    return new Intl.ListFormat(i18n.language, {
      style: "long",
      type: "conjunction",
    }).formatToParts(agreementsMinusCOPPA.map((a) => a.id));
  }, [agreementsMinusCOPPA]);

  const coppaAgreement = useMemo(() => {
    return agreements.find(
      (agreement) => agreement.agreement_type === AgreementType.COPPA
    );
  }, [agreements]);
  return (
    <Text color="primary.dark-gray">
      {t("agreementsCopyInitial", { buttonLabel })}
      &nbsp;
      {agreementsListMinusCOPPA.length > 0 && (
        <>
          {t("agreementsCopy")}
          &nbsp;
          {agreementsListMinusCOPPA.map((item) => {
            if (item.type === "literal") return item.value;
            const agreement = agreements.find((a) => a.id === item.value);
            if (!agreement) return;

            return (
              <Link
                key={agreement.id}
                color="utility.link"
                href={getBrandConditionedAgreementUrl(agreement.agreement_url)}
                target="_blank"
                rel="noreferrer"
              >
                {agreement.title}
              </Link>
            );
          })}
        </>
      )}
      {!coppaAgreement && <>.</>}
      {agreementsListMinusCOPPA.length > 0 && !!coppaAgreement && (
        <>;&nbsp;{t("and")}&nbsp;</>
      )}
      {coppaAgreement && (
        <>
          {t("agreementsCopyCOPPA")}
          &nbsp;
          <Link
            key={coppaAgreement.id}
            color="utility.link"
            href={getBrandConditionedAgreementUrl(coppaAgreement.agreement_url)}
            target="_blank"
            rel="noreferrer"
          >
            {coppaAgreement.title}
          </Link>
          .
        </>
      )}
    </Text>
  );
};
